import { onINP } from 'web-vitals/attribution';
import type { INPMetricWithAttribution } from 'web-vitals/attribution';

import type { Confidence, Trackable } from '@spotify-confidence/sdk';

import { generalizePathname } from './metrics';

// LoAF entries fire for any animation frame >50ms total. Filter on
// blocking work specifically so we capture frames that delayed input
// rather than long but non-blocking frames (e.g. heavy paint).
const LOAF_BLOCKING_DURATION_THRESHOLD_MS = 50;

interface LoafScriptTiming extends PerformanceEntry {
  invoker: string;
  sourceURL: string;
  sourceFunctionName: string;
}

interface LoafEntry extends PerformanceEntry {
  blockingDuration: number;
  scripts: LoafScriptTiming[];
}

const currentRoute = (): string => generalizePathname(window.location.pathname);

const inpTracker = (): Trackable.Manager => controller => {
  onINP(
    (metric: INPMetricWithAttribution) => {
      const { attribution } = metric;
      controller.track('web-vitals-inp', {
        value: metric.value,
        route: currentRoute(),
        interaction_target: attribution.interactionTarget,
        input_delay: attribution.inputDelay,
        processing_duration: attribution.processingDuration,
        presentation_delay: attribution.presentationDelay,
      });
    },
    // Report on every worst-so-far INP increase rather than once at hide.
    // The SDK uses a regular fetch with no keepalive/sendBeacon, so events
    // queued at pagehide get dropped when the page actually unloads.
    { reportAllChanges: true },
  );
};

const loafTracker = (): Trackable.Manager => controller => {
  if (typeof PerformanceObserver === 'undefined') return undefined;
  try {
    const observer = new PerformanceObserver(list => {
      for (const entry of list.getEntries() as LoafEntry[]) {
        if (entry.blockingDuration < LOAF_BLOCKING_DURATION_THRESHOLD_MS)
          continue;
        const worstScript = entry.scripts.reduce<LoafScriptTiming | undefined>(
          (worst, script) =>
            !worst || script.duration > worst.duration ? script : worst,
          undefined,
        );
        controller.track('long-animation-frame', {
          duration: entry.duration,
          blocking_duration: entry.blockingDuration,
          route: currentRoute(),
          script_source: worstScript?.sourceURL,
          script_function: worstScript?.sourceFunctionName,
          script_duration: worstScript?.duration,
          script_invoker: worstScript?.invoker,
        });
      }
    });
    observer.observe({ type: 'long-animation-frame', buffered: true });
    return () => observer.disconnect();
  } catch {
    // long-animation-frame is Chrome 123+; older browsers throw on observe.
    return undefined;
  }
};

export const setupConfidenceTrackers = (api: Confidence): void => {
  api.track(inpTracker());
  api.track(loafTracker());
};
